import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button, Modal, ModalTitle } from 'react-bootstrap';
import PropTypes from 'prop-types';
import {learnRovrData} from './learnRovrData';
import { useHistory } from "react-router-dom";

const LearnRovrModal = React.memo((props) => {
    // Destructure props for better performance and linting compliance
    const { show, setShowLearnRovr, code113Enabled } = props;

    const history = useHistory();
    const [currentItem, setCurrentItem] = useState(show !== null && show !== undefined ? show : null);

    // Sync currentItem with show prop when it changes
    useEffect(() => {
        if (show !== null && show !== undefined) {
            setCurrentItem(show);
        } else {
            setCurrentItem(null);
        }
    }, [show]);

    // Memoized event handlers with useCallback for stable references
    const handleClose = useCallback(() => {
        setShowLearnRovr(null);
        history.push('/');
    }, [setShowLearnRovr, history]);

    const nextItem = useCallback(() => {
        setCurrentItem(prevItem => {
            let current = prevItem;
            let next = current + 1;
            
            // Skip color code section if code113Enabled is false
            if (learnRovrData[current]?.title === 'Color Code') {
                if (!code113Enabled) {
                    next = next + 1;
                }
            }
            
            return next % learnRovrData.length;
        });
    }, [code113Enabled]);

    // Memoized computed values
    const isModalVisible = useMemo(() => {
        return currentItem !== null;
    }, [currentItem]);

    const currentItemData = useMemo(() => {
        if (currentItem === null || !learnRovrData[currentItem]) {
            return { title: '', text: '', img: '' };
        }
        return learnRovrData[currentItem];
    }, [currentItem]);

    const modalTitle = useMemo(() => {
        const { title, img } = currentItemData;
        return (
            <>
                {title}
                {currentItem === 4 ? '' : img}
            </>
        );
    }, [currentItemData, currentItem]);

    // Memoized modal props and styles
    const modalProps = useMemo(() => ({
        dialogClassName: "fullscreenmodal",
        show: isModalVisible,
        backdrop: "static",
        keyboard: false,
        id: "about"
    }), [isModalVisible]);

    const headerTitleStyle = useMemo(() => ({
        className: "aboutHead"
    }), []);

    const modalBodyTitleStyle = useMemo(() => ({
        className: "titleBody stepBody"
    }), []);

    const buttonContainerStyle = useMemo(() => ({
        className: 'stepTourButtons'
    }), []);

    const exitButtonStyle = useMemo(() => ({
        className: "mr-2 buttonSearch submitColor buttonTour"
    }), []);

    const nextButtonStyle = useMemo(() => ({
        className: "mr-2 buttonSearch resetColor buttonTour"
    }), []);

    // Don't render if currentItem is null
    if (currentItem === null) {
        return null;
    }

    return (
        <>
            <Modal {...modalProps}>
                <Modal.Header>
                    <Modal.Title {...headerTitleStyle}>
                        {modalTitle}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body>
                    <ModalTitle {...modalBodyTitleStyle}>
                        {currentItemData.text}
                    </ModalTitle>
                </Modal.Body>
                
                <div {...buttonContainerStyle}>
                    <Button 
                        {...exitButtonStyle}
                        onClick={handleClose}
                    >
                        Exit
                    </Button>
                    <Button 
                        {...nextButtonStyle}
                        onClick={nextItem}
                    >
                        Next
                    </Button>
                </div>
            </Modal>
        </>
    );
});

LearnRovrModal.displayName = 'LearnRovrModal';

LearnRovrModal.propTypes = {
    show: PropTypes.number,
    setShowLearnRovr: PropTypes.func.isRequired,
    code113Enabled: PropTypes.bool
};

export default LearnRovrModal;